import { useState } from 'react';
import EDDialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import { IFormData } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import RepeteraAktivitetForm, {
  getWeekDayName,
  useRepeteraAktivitet,
  validateRepeteraResurser,
} from '../RepeteraAktivitetForm/RepeteraAktivitetForm';
import RepeteraAktivitetFormStep2, {
  IRow,
} from '../RepeteraAktivitetForm/RepeteraAktivitetFormStep2';
import { makeStyles } from 'tss-react/mui';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import repeteraAktivitetMutation from '../../Mutations/RepeteraAktivitetMutation.gql';
import {
  RepeteraAktivitetMutation,
  RepeteraAktivitetMutationVariables,
} from '../../Mutations/_QueryTypes/repeteraAktivitetMutation';
import repeteraPreviewQuery from '../../Queries/RepeteraAktivitetPreview.gql';
import {
  RepeteraAktivitetPreviewQuery,
  RepeteraAktivitetPreviewQueryVariables,
} from '../../Queries/_QueryTypes/RepeteraAktivitetPreviewQuery';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import { handleGQLError } from '../../../../../../../Utils/Error';
import shortid from 'shortid';
import {
  capitalizeFirstLetter,
  dateTimeStringIsValidDate,
  getWeekDayText2,
} from '../../../../../../../Utils/dateUtils';
import {
  dateToStr,
  timeToStr,
  tryStrToDateTime,
} from '../../../../../../../Utils/stringUtils';
import { TEDInputBORepAkt } from '../../../../../../../globalQueryTypes';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import RepeteraAktivitetDubbelbokning from '../RepeteraAktivitetDubbelbokning';
import { checkDoubleBookingInfo } from '../../Utils/AktivitetUtils';
import EDIcon from '../../../../../../../BaseComponents/Icon/EDIcon';
import { useIsSmallScreen } from '../../../../../../../Hooks/useFullscreenView';

const useStyles = makeStyles()(theme => ({
  repeteraDialog: {
    "& [class*='MuiDialogContent-root']": {
      backgroundColor: theme.palette.background.default,
    },
  },
  dialogHeaderTopArea: {
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
}));

interface IProps {
  aktivitet: IFormData;
  open: boolean;
  setOpen: (open: boolean) => void;
  activeDatabase: string | undefined;
}

export const notValidDateSpan = (datumFr: string, datumTi: string): boolean => {
  return new Date(datumFr) > new Date(datumTi);
};

const RepeteraAktivitetDialog: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes } = useStyles();
  const [page, setPage] = useState<0 | 1 | 2 | 3>(0);
  const aktivitetWeekDay = getWeekDayName(props.aktivitet);
  const repeteraAktivitet = useRepeteraAktivitet(aktivitetWeekDay);
  const validations = validateRepeteraResurser(repeteraAktivitet.values);
  const [rows, setRows] = useState<IRow[]>([]);
  const [openDoubleBookingDialog, setOpenDoubleBookingDialog] = useState(false);
  const [previewErrorMessage, setPreviewErrorMessage] = useState('');
  const isSmallScreen = useIsSmallScreen();

  const [repeteraMutation, { loading: isSaving }] = useMutation<
    RepeteraAktivitetMutation,
    RepeteraAktivitetMutationVariables
  >(repeteraAktivitetMutation, {
    refetchQueries: ['AktivitetQuery'],
  });

  const onError = (error: ApolloError) => {
    setPreviewErrorMessage(error.message);
    handleGQLError(error);
  };

  const [getRepeteraPreview, { loading, data: repetera_preview_data }] =
    useLazyQuery<
      RepeteraAktivitetPreviewQuery,
      RepeteraAktivitetPreviewQueryVariables
    >(repeteraPreviewQuery, {
      fetchPolicy: 'network-only',
      onError: onError,
      onCompleted: () => {
        if (repetera_preview_data?.rtn?.BORepeteraAktivitetPreview?.statusOK) {
          const newRows =
            repetera_preview_data?.rtn?.BORepeteraAktivitetPreview?.aktiviteter?.map(
              aktivitet => ({
                id: shortid(),
                ...aktivitet,
                Datum: dateToStr(
                  tryStrToDateTime(aktivitet.DatumFran) ?? new Date()
                ),
                Dag: capitalizeFirstLetter(
                  aktivitet.KortDag ??
                    getWeekDayText2(
                      tryStrToDateTime(aktivitet.DatumFran) ?? new Date()
                    )
                ),
                Tid: `${timeToStr(
                  tryStrToDateTime(aktivitet.DatumFran) ?? new Date()
                )}-${timeToStr(
                  tryStrToDateTime(aktivitet.DatumTill) ?? new Date()
                )}`,
                Plats: aktivitet.Bokningsrubriker?.[0].Resurs?.resurs,
                ...checkDoubleBookingInfo(aktivitet),
              })
            ) ?? [];
          setRows(newRows);

          setPage(1);

          setOpenDoubleBookingDialog(
            Boolean(
              repetera_preview_data?.rtn?.BORepeteraAktivitetPreview?.aktiviteter?.find(
                akt =>
                  Boolean(
                    akt?.Bokningsrubriker?.find(
                      res => res.Resurs?.ResBokad !== null
                    )
                  ) ||
                  Boolean(akt?.Resurser?.find(res => res?.ResBokad !== null)) // övriga resurser
              )
            ) ||
              Boolean(
                repetera_preview_data?.rtn?.BORepeteraAktivitetPreview?.aktiviteter?.find(
                  akt =>
                    Boolean(
                      akt?.Bokningsrubriker?.find(
                        res => res.Resurs?.XBridge !== null
                      )
                    ) ||
                    Boolean(akt?.Resurser?.find(res => res?.XBridge !== null)) // övriga resurser
                )
              )
          );
        }
      },
    });

  const closeDialog = () => {
    props.setOpen(false);
  };

  const closeDoubleBookingDialog = () => {
    setOpenDoubleBookingDialog(false);
  };

  const goBack = () => {
    setPage(0);
  };

  const nextDialog = async () => {
    if (page === 0) {
      getRepeteraPreview({
        variables: {
          dbname: props.activeDatabase ?? '',
          aktId: props.aktivitet.id,
          installningar: {
            DatumFran: repeteraAktivitet.values.datumFr,
            DatumTill: repeteraAktivitet.values.datumTi,
            Period: repeteraAktivitet.values.weekValue?.value ?? 0,
            Veckodagar: {
              Man: repeteraAktivitet.values.mondayChecked,
              Tis: repeteraAktivitet.values.tuesdayChecked,
              Ons: repeteraAktivitet.values.wednesdayChecked,
              Tor: repeteraAktivitet.values.thursdayChecked,
              Fre: repeteraAktivitet.values.fridayChecked,
              Lor: repeteraAktivitet.values.saturdayChecked,
              Son: repeteraAktivitet.values.sundayChecked,
            },
            BokaHelg: true,
          },
        },
      });
    } else {
      const allRepetaradeAktiviteter = rows.map(
        (item: IRow): TEDInputBORepAkt => ({
          Typ: item.Typ,
          BokGrupp: item.BokGrupp,
          Boknr: item.Boknr,
          Rubrik: item.Rubrik,
          AktID: item.AktID,
          DatumFran: item.DatumFran,
          DatumTill: item.DatumTill,
          Vald: item.Vald,
          Aktrubriker: item.Aktrubriker?.map(aktrub => ({
            ResRubrikID: aktrub.ResRubrikID,
            PosNR: aktrub.PosNR,
          })),
          Bokningsrubriker: item.Bokningsrubriker?.map(bokrub => ({
            RestypID: bokrub.RestypID,
            Rubrik: bokrub.Rubrik,
            RubrikID: bokrub.RubrikID,
            PosNR: bokrub.PosNR,
            Resurs: bokrub.Resurs
              ? {
                  resurs: bokrub.Resurs.resurs,
                  resursIDText: bokrub.Resurs.resursIDText,
                  resursID: bokrub.Resurs.resursID,
                  DatumFran: bokrub.Resurs.DatumFran,
                  DatumTill: bokrub.Resurs.DatumTill,
                  Vald: item.Vald,
                  XBResursEmail: bokrub.Resurs.XBResursEmail,
                  XBSynk: bokrub.Resurs.XBSynk,
                  ResBokad: bokrub.Resurs.ResBokad,
                  Planerad: bokrub.Resurs.Planerad,
                  XBridge: bokrub.Resurs.XBridge,
                  FlexLedig: bokrub.Resurs.FlexLedig,
                  VarmeStandard: bokrub.Resurs.VarmeStandard,
                }
              : undefined,
          })),
          Resurser: item.Resurser?.map(res => ({
            resurs: res.resurs,
            resursIDText: res.resursIDText,
            resursID: res.resursID,
            DatumFran: res.DatumFran,
            DatumTill: res.DatumTill,
            Vald: item.Vald,
            XBResursEmail: res.XBResursEmail,
            XBSynk: res.XBSynk,
            ResBokad: res.ResBokad,
            Planerad: res.Planerad,
            XBridge: res.XBridge,
            FlexLedig: res.FlexLedig,
            VarmeStandard: res.VarmeStandard,
          })),
          KnutnaAktiviteter: item.KnutnaAktiviteter?.map(knut => ({
            Typ: knut.Typ,
            BokGrupp: knut.BokGrupp,
            Boknr: knut.Boknr,
            Rubrik: knut.Rubrik,
            AktID: knut.AktID,
            DatumFran: knut.DatumFran,
            DatumTill: knut.DatumTill,
            Vald: item.Vald,
            Aktrubriker: knut.Aktrubriker?.map(aktrub => ({
              ResRubrikID: aktrub.ResRubrikID,
              PosNR: aktrub.PosNR,
            })),
            Bokningsrubriker: knut.Bokningsrubriker?.map(bokrub => ({
              RestypID: bokrub.RestypID,
              Rubrik: bokrub.Rubrik,
              RubrikID: bokrub.RubrikID,
              PosNR: bokrub.PosNR,
              Resurs: bokrub.Resurs
                ? {
                    resurs: bokrub.Resurs.resurs,
                    resursIDText: bokrub.Resurs.resursIDText,
                    resursID: bokrub.Resurs.resursID,
                    DatumFran: bokrub.Resurs.DatumFran,
                    DatumTill: bokrub.Resurs.DatumTill,
                    Vald: item.Vald,
                    XBResursEmail: bokrub.Resurs.XBResursEmail,
                    XBSynk: bokrub.Resurs.XBSynk,
                    ResBokad: bokrub.Resurs.ResBokad,
                    Planerad: bokrub.Resurs.Planerad,
                    XBridge: bokrub.Resurs.XBridge,
                    FlexLedig: bokrub.Resurs.FlexLedig,
                    VarmeStandard: bokrub.Resurs.VarmeStandard,
                  }
                : undefined,
            })),
            Resurser: knut.Resurser?.map(res => ({
              resurs: res.resurs,
              resursIDText: res.resursIDText,
              resursID: res.resursID,
              DatumFran: res.DatumFran,
              DatumTill: res.DatumTill,
              Vald: item.Vald,
              XBResursEmail: res.XBResursEmail,
              XBSynk: res.XBSynk,
              ResBokad: res.ResBokad,
              Planerad: res.Planerad,
              XBridge: res.XBridge,
              FlexLedig: res.FlexLedig,
              VarmeStandard: res.VarmeStandard,
            })),
          })),
        })
      );

      await repeteraMutation({
        variables: {
          aktId: props.aktivitet.id,
          dbname: props.activeDatabase ?? '',
          aktiviteter: allRepetaradeAktiviteter,
        },
      });
      props.setOpen(false);
    }
  };

  const isInvalidDate = (date: string): boolean => {
    if (!date) {
      return true;
    }
    const removeHyphensFromDate = date.replaceAll('-', '');
    return !dateTimeStringIsValidDate(removeHyphensFromDate);
  };

  let isEveryCheckboxUnchecked =
    rows.every(checkbox => !checkbox.Vald) && page === 1;

  return (
    <EDDialog
      title="Repetera aktivitet"
      subtitle={page === 0 ? 'Steg 1 av 2' : 'Steg 2 av 2'}
      open={props.open}
      onClose={closeDialog}
      footerActions={[
        {
          label: page === 1 ? 'Tillbaka' : '',
          onClick: goBack,
          buttonProps: { disabled: loading || isSaving },
        },
        {
          label: 'Avbryt',
          onClick: closeDialog,
          buttonProps: { disabled: loading || isSaving },
        },
        {
          label: page === 1 ? 'Slutför' : 'Nästa',
          onClick: nextDialog,
          buttonProps: {
            disabled:
              loading ||
              isSaving ||
              isInvalidDate(repeteraAktivitet.values.datumFr) ||
              isInvalidDate(repeteraAktivitet.values.datumTi) ||
              notValidDateSpan(
                repeteraAktivitet.values.datumFr,
                repeteraAktivitet.values.datumTi
              ) ||
              validations.length > 0 ||
              isEveryCheckboxUnchecked,

            disabledReason: isEveryCheckboxUnchecked ? (
              <p>Välj minst en repetition för att slutföra</p>
            ) : (
              validations.map(validation => (
                <p key={validation.fieldName}> {validation.message}</p> //←← this is to return disabled reasons on a new line each
              ))
            ),
          },
        },
      ]}
      maxWidth={false}
      className={classes.repeteraDialog}
    >
      <EDBox sx={{ position: isSmallScreen ? 'static' : 'relative' }}>
        {(loading || isSaving) && <EDAvenyProgress overlay />}
        {page === 0 && (
          <>
            <RepeteraAktivitetForm
              aktivitet={props.aktivitet}
              values={repeteraAktivitet.values}
              setters={repeteraAktivitet.setters}
              isInvalidDate={isInvalidDate}
              notValidDateSpan={notValidDateSpan}
            />
            <EDDialog
              title="Repetera aktivitet"
              subtitle={'Varning'}
              open={previewErrorMessage !== ''}
              onClose={() => {
                setPreviewErrorMessage('');
              }}
              footerActions={[
                {
                  label: 'Avbryt',
                  onClick: () => {
                    setPreviewErrorMessage('');
                  },
                },
              ]}
              maxWidth={false}
              className={classes.repeteraDialog}
            >
              <EDBox>
                {
                  repetera_preview_data?.rtn?.BORepeteraAktivitetPreview
                    ?.meddelande
                }
              </EDBox>
            </EDDialog>
          </>
        )}
        {page === 1 &&
          repetera_preview_data?.rtn?.BORepeteraAktivitetPreview?.statusOK && (
            <>
              <RepeteraAktivitetFormStep2
                aktivitet={props.aktivitet}
                values={repeteraAktivitet.values}
                setters={repeteraAktivitet.setters}
                rows={rows}
                setRows={setRows}
              />

              <EDDialog
                open={openDoubleBookingDialog}
                title="Dubbelbokning"
                headerProps={{
                  headerIcon: (
                    <EDIcon
                      marginRight={3}
                      iconType="warning"
                      color="warning"
                      size="large"
                    />
                  ),
                  classes: {
                    topArea: classes.dialogHeaderTopArea,
                  } as any,
                }}
                footerActions={[
                  {
                    label: 'Stäng',
                    onClick: closeDoubleBookingDialog,
                    buttonProps: {
                      disabled: validations.length > 0,
                      disabledReason: validations.map(validation => (
                        <p key={validation.fieldName}> {validation.message}</p> //←← this is to return disabled reasons on a new line each
                      )),
                    },
                  },
                ]}
                maxWidth={false}
                className={classes.repeteraDialog}
              >
                <RepeteraAktivitetDubbelbokning
                  aktivitet={props.aktivitet}
                  values={repeteraAktivitet.values}
                  setters={repeteraAktivitet.setters}
                  rows={rows}
                  setRows={setRows}
                />
              </EDDialog>
            </>
          )}
      </EDBox>
    </EDDialog>
  );
  //↓↓ page 1 of 'Repetera Aktivitet'
};

export default RepeteraAktivitetDialog;
